class StickyProductCart extends HTMLElement {
  connectedCallback() {
    this.initializeAttempts = 0;
    this.initialize();
  }

  initialize() {
    if (this.initialized) return;

    this.productInformation = document.querySelector(this.dataset.productInformation);
    this.form = document.getElementById(this.dataset.formId);
    this.card = this.querySelector('[data-sticky-cart-card]');
    this.submitButton = this.querySelector('[data-sticky-cart-submit]');
    this.titleElement = this.querySelector('[data-sticky-cart-title]');
    this.price = this.querySelector('[data-sticky-cart-price]');
    this.image = this.querySelector('[data-sticky-cart-image]');
    this.footer = document.querySelector('.footer-group');
    this.isPastPrimaryButton = false;
    this.isFooterVisible = false;
    this.isVisibilityReady = false;
    this.syncFrame = null;

    // Shopify can insert this section before its product form after an in-page
    // product navigation. Wait for the product information instead of leaving
    // the sticky cart permanently uninitialized.
    if (!this.productInformation || !this.card || !this.submitButton) {
      if (this.initializeAttempts < 20) {
        this.initializeAttempts += 1;
        this.initializeTimer = window.setTimeout(() => this.initialize(), 50);
      }
      return;
    }

    this.initialized = true;

    this.onStickyClick = this.onStickyClick.bind(this);
    this.onVariantUpdated = this.scheduleSync.bind(this);
    this.onProductMutation = this.scheduleSync.bind(this);
    this.onPagePositionChange = this.updatePosition.bind(this);

    this.submitButton.addEventListener('click', this.onStickyClick);
    window.eventBus?.on('variant:updated', this.onVariantUpdated);
    window.eventBus?.on('cart:added', this.onVariantUpdated);
    window.eventBus?.on('drawer:closed', this.onVariantUpdated);
    this.productInformation.addEventListener('change', this.onProductMutation);
    this.productObserver = new MutationObserver(this.onProductMutation);
    this.productObserver.observe(this.productInformation, { childList: true, subtree: true });
    window.addEventListener('scroll', this.onPagePositionChange, { passive: true });
    window.addEventListener('resize', this.onPagePositionChange, { passive: true });
    window.addEventListener('pageshow', this.onPagePositionChange);

    this.createObservers();
    this.sync();
    this.updatePosition();
  }

  disconnectedCallback() {
    window.clearTimeout(this.initializeTimer);
    this.initialized = false;
    this.submitButton?.removeEventListener('click', this.onStickyClick);
    window.eventBus?.off('variant:updated', this.onVariantUpdated);
    window.eventBus?.off('cart:added', this.onVariantUpdated);
    window.eventBus?.off('drawer:closed', this.onVariantUpdated);
    this.productInformation?.removeEventListener('change', this.onProductMutation);
    this.productObserver?.disconnect();
    this.primaryObserver?.disconnect();
    this.footerObserver?.disconnect();
    window.removeEventListener('scroll', this.onPagePositionChange);
    window.removeEventListener('resize', this.onPagePositionChange);
    window.removeEventListener('pageshow', this.onPagePositionChange);
    cancelAnimationFrame(this.syncFrame);
    document.documentElement.classList.remove('lbr-product-sticky-cart-visible');
    document.documentElement.style.removeProperty('--lbr-product-sticky-cart-height');
  }

  scheduleSync() {
    cancelAnimationFrame(this.syncFrame);
    this.syncFrame = requestAnimationFrame(() => {
      this.createObservers();
      this.sync();
      this.updatePosition();
    });
  }

  createObservers() {
    const primaryButton = this.productInformation?.querySelector('.product--add-to-cart-button');
    if (!primaryButton || primaryButton === this.primaryButton) return;

    this.primaryButton = primaryButton;
    this.primaryObserver?.disconnect();
    this.isPastPrimaryButton = primaryButton.getBoundingClientRect().bottom <= 0;
    this.primaryObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isPastPrimaryButton = entry.boundingClientRect.bottom <= 0;
        this.revealWhenReady();
      });
    }, { threshold: 0 });
    this.primaryObserver.observe(primaryButton);

    if (this.footer && !this.footerObserver) {
      this.footerObserver = new IntersectionObserver((entries) => {
        this.isFooterVisible = entries.some((entry) => entry.isIntersecting);
        this.updateVisibility();
      }, { threshold: 0 });
      this.footerObserver.observe(this.footer);
    }

    this.updateVisibility();
  }

  onStickyClick() {
    const form = this.getForm();
    if (this.submitButton.disabled || !form) return;
    form.requestSubmit();
  }

  getForm() {
    this.form = document.getElementById(this.dataset.formId) || this.form;
    return this.form;
  }

  getCurrentVariant() {
    const productForm = this.productInformation.querySelector('product-form');
    const form = this.getForm();
    const variantId = Number(form?.querySelector('[name="id"]')?.value);
    try {
      const variants = JSON.parse(productForm?.dataset.productVariants || '[]');
      return variants.find((variant) => Number(variant.id) === variantId);
    } catch (error) {
      return null;
    }
  }

  getExtrasTotal() {
    return Array.from(this.productInformation.querySelectorAll('[data-cross-sell-selector]:checked'))
      .reduce((total, input) => total + Number(input.dataset.crossSellPrice || 0), 0);
  }

  formatMoney(cents) {
    return window.wetheme?.formatMoney
      ? window.wetheme.formatMoney(cents, this.dataset.moneyFormat)
      : `$${(Number(cents) / 100).toFixed(2)}`;
  }

  sync() {
    const variant = this.getCurrentVariant();
    const mainButton = this.productInformation.querySelector('.product--add-to-cart-button');
    const isAvailable = mainButton ? !mainButton.disabled : Boolean(variant?.available);
    const total = Number(variant?.price || 0) + this.getExtrasTotal();

    this.submitButton.disabled = !isAvailable;
    this.submitButton.textContent = mainButton?.dataset.bundleButtonLabel || (isAvailable ? this.dataset.addLabel : this.dataset.soldOutLabel);
    if (this.titleElement) this.titleElement.textContent = this.dataset.productTitle;
    // The theme formatter returns a money span; preserve its markup instead of showing it as text.
    if (this.price && variant) this.price.innerHTML = this.formatMoney(total);
    const media = variant?.featured_media?.preview_image || variant?.featured_media;
    if (this.image && media?.src) {
      this.image.src = media.src;
      this.image.alt = media.alt || this.dataset.productTitle;
    }
  }

  updateVisibility() {
    if (!this.isVisibilityReady) return;
    this.classList.toggle('is-visible', this.isPastPrimaryButton && !this.isFooterVisible);
    this.syncRewardsOffset();
  }

  syncRewardsOffset() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const shouldOffsetRewards = isMobile && this.classList.contains('is-visible');
    const root = document.documentElement;

    root.classList.toggle('lbr-product-sticky-cart-visible', shouldOffsetRewards);
    if (shouldOffsetRewards) {
      root.style.setProperty('--lbr-product-sticky-cart-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
    } else {
      root.style.removeProperty('--lbr-product-sticky-cart-height');
    }
  }

  revealWhenReady() {
    if (!this.isVisibilityReady) {
      // IntersectionObserver runs after layout has settled. Do not reveal the
      // cart using an earlier, transient button position during page load.
      this.isVisibilityReady = true;
      this.removeAttribute('hidden');
    }
    this.updateVisibility();
  }

  updatePosition() {
    const currentPrimaryButton = this.productInformation?.querySelector('.product--add-to-cart-button');
    if (currentPrimaryButton && currentPrimaryButton !== this.primaryButton) {
      this.primaryButton = null;
      this.createObservers();
    }

    if (!this.primaryButton) return;

    const buttonRect = this.primaryButton.getBoundingClientRect();
    this.isPastPrimaryButton = buttonRect.bottom <= 0;

    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      this.isFooterVisible = footerRect.top < window.innerHeight && footerRect.bottom > 0;
    }

    this.updateVisibility();
  }
}

if (!customElements.get('sticky-product-cart')) {
  customElements.define('sticky-product-cart', StickyProductCart);
}

window.wetheme?.webcomponentRegistry?.register({ key: 'component-sticky-product-cart' });
